import React, { FC, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { SaveButton } from "components/buttons/SaveButton"
import { InputField } from "components/inputs/InputField"
import { Toggle } from "components/toggles/Toggle"

import { postbacksService } from "../../services/postbacks/postbacks.service"
import { AttributionMode, TrackingConfig } from "../../services/postbacks/type"

export const AliasesConfig: FC = () => {
  const { t } = useTranslation(["translation"], { useSuspense: false })
  const [enabled, setEnabled] = useState(true)
  const [aliases, setAliases] = useState<string[]>([])
  const [mode, setMode] = useState<AttributionMode>("last_click")
  const [maxAttempts, setMaxAttempts] = useState(3)
  const [newAlias, setNewAlias] = useState("")
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const load = async () => {
    setLoading(true)
    const cfg = await postbacksService.getConfig()
    if (cfg) {
      setEnabled(cfg.enabled)
      setAliases(cfg.aliases)
      setMode(cfg.attribution_mode)
      setMaxAttempts(cfg.max_attempts)
    }
    setLoading(false)
  }

  useEffect(() => {
    void load()
  }, [])

  const addAlias = () => {
    const value = newAlias.trim()
    if (!value || aliases.includes(value)) return
    setAliases([...aliases, value])
    setNewAlias("")
  }

  const removeAlias = (alias: string) => setAliases(aliases.filter((a) => a !== alias))

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir
    if (target < 0 || target >= aliases.length) return
    const next = [...aliases]
    ;[next[index], next[target]] = [next[target], next[index]]
    setAliases(next)
  }

  const save = async () => {
    setSaving(true)
    const saved = await postbacksService.updateConfig({
      enabled,
      aliases,
      attribution_mode: mode,
      max_attempts: maxAttempts
    } as TrackingConfig)
    if (saved) {
      setEnabled(saved.enabled)
      setAliases(saved.aliases)
      setMode(saved.attribution_mode)
      setMaxAttempts(saved.max_attempts)
    }
    setSaving(false)
  }

  if (loading)
    return (
      <div className="rounded-xl border border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)] bg-white dark:bg-[#0C0C1A] p-6">
        {t("s2sPostbacks.aliases.loading")}
      </div>
    )

  return (
    <div className="rounded-xl border border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)] bg-white dark:bg-[#0C0C1A] p-6">
      <div className={`mb-5 flex items-center gap-3 rounded-lg px-4 py-3 bg-[rgba(0,0,0,0.03)] dark:bg-[rgba(255,255,255,0.04)]`}>
        <Toggle value={enabled} handler={setEnabled} />
        <span className="text-sm">
          <span className="font-semibold">{t("s2sPostbacks.aliases.enabledLabel")}</span>
          <span className="ml-2 opacity-60">{t("s2sPostbacks.aliases.enabledHint")}</span>
        </span>
      </div>

      <h2 className="mb-1 text-lg font-semibold">{t("s2sPostbacks.aliases.title")}</h2>
      <p className="mb-4 text-sm opacity-60">{t("s2sPostbacks.aliases.description")}</p>

      <ul className="mb-4 flex flex-col gap-2">
        {aliases.map((alias, index) => (
          <li key={alias} className={`flex items-center gap-2 rounded-lg px-3 py-2 bg-[rgba(0,0,0,0.03)] dark:bg-[rgba(255,255,255,0.04)]`}>
            <span className="w-6 text-xs opacity-50">{index + 1}.</span>
            <span className="flex-1 font-mono text-sm">{alias}</span>
            <button className="btn btn-ghost btn-xs" onClick={() => move(index, -1)} title={t("s2sPostbacks.aliases.moveUp")}>
              ↑
            </button>
            <button className="btn btn-ghost btn-xs" onClick={() => move(index, 1)} title={t("s2sPostbacks.aliases.moveDown")}>
              ↓
            </button>
            <button
              className="btn btn-ghost btn-xs text-rose-400"
              onClick={() => removeAlias(alias)}
              title={t("s2sPostbacks.aliases.remove")}
            >
              ✕
            </button>
          </li>
        ))}
        {aliases.length === 0 && <li className="text-sm opacity-50">{t("s2sPostbacks.aliases.empty")}</li>}
      </ul>

      <div className="mb-6 flex items-end gap-2">
        <InputField
          value={newAlias}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewAlias(e.target.value)}
          placeholder={t("s2sPostbacks.aliases.addPlaceholder")}
        />
        <button className="btn btn-primary" onClick={addAlias} type="button">
          {t("s2sPostbacks.aliases.add")}
        </button>
      </div>

      <h3 className="mb-1 text-sm font-semibold">{t("s2sPostbacks.aliases.attributionTitle")}</h3>
      <p className="mb-3 text-sm opacity-60">{t("s2sPostbacks.aliases.attributionDescription")}</p>
      <div className="mb-6 flex gap-6 text-sm">
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="radio"
            className="radio radio-sm radio-primary"
            checked={mode === "last_click"}
            onChange={() => setMode("last_click")}
          />
          {t("s2sPostbacks.aliases.lastClick")}
        </label>
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="radio"
            className="radio radio-sm radio-primary"
            checked={mode === "first_click"}
            onChange={() => setMode("first_click")}
          />
          {t("s2sPostbacks.aliases.firstClick")}
        </label>
      </div>

      <h3 className="mb-1 text-sm font-semibold">{t("s2sPostbacks.aliases.maxAttemptsTitle")}</h3>
      <p className="mb-2 text-sm opacity-60">{t("s2sPostbacks.aliases.maxAttemptsHint")}</p>
      <input
        type="number"
        min={1}
        max={10}
        className="input input-bordered input-sm mb-6 w-24 bg-[rgba(0,0,0,0.05)] dark:bg-[rgba(0,0,0,0.2)]"
        value={maxAttempts}
        onChange={(e) => setMaxAttempts(Math.min(10, Math.max(1, Number(e.target.value) || 1)))}
      />

      <SaveButton onClick={save} loading={saving}>
        {t("s2sPostbacks.aliases.save")}
      </SaveButton>
    </div>
  )
}
